import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';
import { db } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import useSEO from '../hooks/useSEO';

function ExecutivesPage({ logo }) {
  useSEO({
    title: 'Veydra for Executives',
    description: 'Simulation-driven decision intelligence for executive leaders. Test strategies and see consequences before acting.'
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await addDoc(collection(db, 'form-submissions-incremental'), {
        ...formData,
        source: 'executives',
        page: '/executives',
        intent: 'consultation',
        createdAt: new Date(),
        lastUpdated: new Date()
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Centered Logo */}
      <div className="flex justify-center pt-8 pb-4">
        <Link to="/">
          <img src={logo} className="h-8 w-8 md:h-10 md:w-10" alt="Veydra logo" />
        </Link>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-3xl mx-auto text-center">
          {/* Hero */}
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Make Better Strategic Decisions with Systems Thinking
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            Transform complex business challenges into clear, actionable insights. See the ripple effects of your decisions before you make them.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <a 
              href="https://app.veydra.io/register" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <button className="bg-black text-white py-4 px-8 rounded-lg font-semibold hover:bg-gray-800 transition-colors text-lg w-full sm:w-auto min-w-[200px]">
                Try It Free
              </button>
            </a>
            
            <a href="tel:+18885434111" className="inline-block">
              <button className="border-2 border-gray-300 text-gray-700 py-4 px-8 rounded-lg font-semibold hover:border-gray-400 hover:bg-gray-50 transition-colors text-lg w-full sm:w-auto min-w-[200px]">
                Call Now
              </button>
            </a>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-50 rounded-xl p-8 max-w-md mx-auto border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Schedule a Consultation</h3>
            
            {submitStatus === 'success' ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">Thank you!</h4>
                <p className="text-gray-600">We'll be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {submitStatus === 'error' && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    Something went wrong. Please try again.
                  </div>
                )}
                
                {/* Full Name */}
                <div className="mb-4">
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full Name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Email */}
                <div className="mb-4">
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Phone */}
                <div className="mb-6">
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Submit Button */}
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Get Started'}
                </button>
              </form>
            )}
          </div>

          {/* Feature Highlight */}
          <div className="mt-16 pt-16 border-t border-gray-200">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 md:p-12">
              <div className="text-sm font-semibold text-blue-600 mb-3 uppercase tracking-wide">
                Strategic Decision Intelligence
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                See Your Strategy in Action
              </h2>
              <p className="text-xl text-gray-700 mb-6">
                Build dynamic models that capture the complexity of your business environment. Test strategies, understand trade-offs, and align your team around data-driven decisions.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">Visualize</div>
                  <p className="text-gray-600">Map complex relationships clearly</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">Simulate</div>
                  <p className="text-gray-600">Test scenarios risk-free</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">Decide</div>
                  <p className="text-gray-600">Move forward with confidence</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ExecutivesPage;
